import { useState, useEffect } from "react";
import { FaSync } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import "./UpdateInventoryCSV.css";
import { cloudApi } from "../../api";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

const UpdateInventoryCSV = () => {
  const [file, setFile] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [message, setMessage] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStats, setProcessingStats] = useState({
    current: 0, total: 0, updated: 0, skipped: 0, chunk: 0,
  });
  const [pollCount, setPollCount] = useState(0);
  const [pollInterval, setPollInterval] = useState(2000);
  const [syncing, setSyncing] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setMessage("");
    setErrorDetails([]);
    setUploadProgress(0);
    setUploadStatus("idle");
    setProcessingProgress(0);
    setProcessingStats({ current: 0, total: 0, updated: 0, skipped: 0, chunk: 0 });
    setPollCount(0);
    setPollInterval(2000);
    setSyncing(false);
    setSyncComplete(false);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (!selectedFile.name.endsWith(".csv") && !selectedFile.name.endsWith(".xlsx")) {
      setMessage("⚠️ Please upload a .csv or .xlsx file.");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setMessage(`📁 Selected: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)`);
  };

  // ── Trigger full sync on desktop after successful CSV update ──
  // The CSV was processed on the cloud. Pull updated inventory
  // down to SQLite immediately so the POS reflects changes.
  const triggerDesktopSync = async (updatedCount, skippedCount, errorCount) => {
    if (!IS_DESKTOP) {
      setMessage(`✅ Updated: ${updatedCount} | Skipped: ${skippedCount} | Errors: ${errorCount}`);
      return;
    }

    setSyncing(true);
    setSyncComplete(false);
    setMessage(`✅ Updated: ${updatedCount} | Skipped: ${skippedCount} | Errors: ${errorCount}`);

    try {
      await api.post("/sync/now");
      setSyncComplete(true);
      setMessage(`✅ Updated: ${updatedCount} | Skipped: ${skippedCount} | Errors: ${errorCount} — Local database updated.`);
    } catch {
      setSyncComplete(true);
      setMessage(`✅ Updated: ${updatedCount} | Skipped: ${skippedCount} | Errors: ${errorCount} — Sync will complete automatically.`);
    } finally {
      setSyncing(false);
    }
  };

  const checkTaskStatus = async (taskId, retryCount = 0) => {
    const MAX_RETRIES = 5;
    const BASE_INTERVAL = 2000;

    try {
      const res = await cloudApi.get(`/pharmacies/task-status/${taskId}`);
      const data = res.data;

      if (retryCount > 0) {
        retryCount = 0;
        setPollInterval(BASE_INTERVAL);
      }

      if (data.status === "PROCESSING" || data.state === "PROGRESS") {
        setUploadStatus("processing");
        setProcessingProgress(data.progress || 0);
        setProcessingStats({
          current: data.current || 0,
          total: data.total || 0,
          updated: data.updated || 0,
          skipped: data.skipped || 0,
          chunk: data.chunk || 0,
        });
        setMessage(data.message || "⏳ Processing file...");
        setPollCount((prev) => prev + 1);

        let nextInterval = BASE_INTERVAL;
        if (data.progress && data.progress < 50) nextInterval = 2000;
        else if (data.progress && data.progress < 80) nextInterval = 3000;
        else nextInterval = 4000;
        setPollInterval(nextInterval);

        setTimeout(() => checkTaskStatus(taskId, 0), nextInterval);
      }
      else if (data.status === "SUCCESS") {
        const result = data.result || {};
        const updatedCount = result.success_count || result.updated || data.updated || 0;
        const skippedCount = result.skipped_count || result.skipped || data.skipped || 0;
        const errorCount   = result.errors?.length || data.errors?.length || 0;

        setUploadStatus("complete");
        setProcessingProgress(100);
        setErrorDetails(result.errors || data.errors || []);
        setUpdating(false);

        await triggerDesktopSync(updatedCount, skippedCount, errorCount);
      }
      else if (data.status === "FAILURE") {
        setUploadStatus("error");
        setProcessingProgress(0);
        setMessage(data.message || `❌ Update failed`);
        setErrorDetails([]);
        setUpdating(false);
      }
      else if (data.status === "PENDING") {
        setUploadStatus("processing");
        setProcessingProgress(0);
        setMessage(data.message || "⏳ Task queued, waiting to start...");
        setPollCount((prev) => prev + 1);
        setTimeout(() => checkTaskStatus(taskId, 0), 3000);
      }
      else {
        setMessage(data.message || `⚠️ Status: ${data.status}`);
        setTimeout(() => checkTaskStatus(taskId, 0), BASE_INTERVAL);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        if (retryCount < MAX_RETRIES) {
          const backoffInterval = BASE_INTERVAL * Math.pow(2, retryCount);
          setMessage(`⚠️ Server busy, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => checkTaskStatus(taskId, retryCount + 1), backoffInterval);
        } else {
          setUploadStatus("error");
          setProcessingProgress(0);
          setMessage("❌ Server is overloaded. Please try again later.");
          setUpdating(false);
        }
      } else {
        setUploadStatus("error");
        setProcessingProgress(0);
        setMessage(`❌ Failed to check task status: ${err?.message || err}`);
        setUpdating(false);
      }
    }
  };

  const handleUpdate = async () => {
    if (!file) {
      setMessage("⚠️ Please select a CSV or Excel file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUpdating(true);
      setUploadStatus("uploading");
      setUploadProgress(0);
      setMessage("Uploading file...");
      setErrorDetails([]);
      setProcessingProgress(0);
      setProcessingStats({ current: 0, total: 0, updated: 0, skipped: 0, chunk: 0 });
      setPollCount(0);
      setPollInterval(2000);
      setSyncing(false);
      setSyncComplete(false);

      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setMessage(`📤 Uploading... ${percentCompleted}%`);
        },
      };

      const response = await cloudApi.put("/pharmacies/update-inventory-csv", formData, config);
      const taskId = response.data.task_id;

      if (!taskId) {
        setUploadStatus("complete");
        setUploadProgress(100);
        setMessage("✅ Update request submitted, but no task ID returned.");
        setUpdating(false);
        return;
      }

      setUploadProgress(100);
      setUploadStatus("processing");
      setProcessingProgress(0);
      setMessage("✅ File uploaded. Processing started...");
      checkTaskStatus(taskId);
    } catch (err) {
      setUploadStatus("error");
      setUploadProgress(0);
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "❌ Failed to update inventory. Please try again.";
      setMessage(errorMsg);
      setUpdating(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setMessage("");
    setErrorDetails([]);
    setUploadProgress(0);
    setProcessingProgress(0);
    setUploadStatus("idle");
    setProcessingStats({ current: 0, total: 0, updated: 0, skipped: 0, chunk: 0 });
    setPollCount(0);
    setPollInterval(2000);
    setUpdating(false);
    setSyncing(false);
    setSyncComplete(false);
  };

  return (
    <div className="layout">
      {!isMobile && <Sidebar />}
      <div className="main-content">
        <h1 className="title">🔄 Update Inventory CSV / Excel</h1>

        {message && (
          <p className={`upload-message ${uploadStatus}`}>{message}</p>
        )}

        {/* ── Desktop sync status banner ── */}
        {IS_DESKTOP && (syncing || syncComplete) && (
          <div style={{
            background: syncing ? "#d1ecf1" : "#d4edda",
            border: `1px solid ${syncing ? "#bee5eb" : "#c3e6cb"}`,
            borderRadius: "8px",
            padding: "0.85rem 1rem",
            marginBottom: "1rem",
            fontSize: "0.9rem",
            color: syncing ? "#0c5460" : "#155724",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}>
            {syncing ? (
              <span style={{
                display: "inline-block", width: "18px", height: "18px",
                border: "2px solid #bee5eb", borderTop: "2px solid #0c5460",
                borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0,
              }} />
            ) : (
              <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>✅</span>
            )}
            <div>
              <strong style={{ display: "block", marginBottom: "2px" }}>
                {syncing ? "Syncing updated inventory to local database..." : "Local database updated successfully"}
              </strong>
              <span style={{ fontSize: "0.82rem", opacity: 0.85 }}>
                {syncing
                  ? "This may take a moment. You can still use the app while this runs."
                  : "Your POS and inventory now reflect the latest changes."}
              </span>
            </div>
          </div>
        )}

        {IS_DESKTOP && syncing && (
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        )}

        {/* Upload Progress Bar */}
        {uploadStatus !== "idle" && uploadProgress > 0 && uploadProgress < 100 && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className={`progress-fill uploading`} style={{ width: `${uploadProgress}%` }}>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            </div>
            <p className="progress-label">📤 Uploading file...</p>
          </div>
        )}

        {/* Processing Progress Bar */}
        {(uploadStatus === "processing" || uploadStatus === "complete") && (
          <div className="progress-container processing-container">
            <div className="progress-bar">
              {uploadStatus === "processing" ? (
                <div className={`progress-fill ${uploadStatus} indeterminate`}>
                  <span className="progress-text">⏳</span>
                </div>
              ) : (
                <div className={`progress-fill ${uploadStatus}`} style={{ width: `${processingProgress}%` }}>
                  <span className="progress-text">{processingProgress}%</span>
                </div>
              )}
            </div>

            {/* ── Processing Stats — large visible cards ── */}
            {processingStats.total > 0 && (
              <div style={{
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "0.85rem 1rem",
                marginTop: "0.75rem",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                gap: "0.75rem",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#333" }}>
                    {processingStats.current}/{processingStats.total}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#666", marginTop: "2px" }}>Rows</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#27ae60" }}>
                    {processingStats.updated}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#666", marginTop: "2px" }}>Updated</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#ff9800" }}>
                    {processingStats.skipped}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#666", marginTop: "2px" }}>Skipped</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#007bff" }}>
                    {processingStats.chunk}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#666", marginTop: "2px" }}>Chunk</div>
                </div>
              </div>
            )}

            {/* ── Poll info — visible pills ── */}
            {pollCount > 0 && uploadStatus === "processing" && (
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.65rem", flexWrap: "wrap" }}>
                <span style={{
                  background: "#e9ecef", borderRadius: "20px",
                  padding: "0.25rem 0.75rem", fontSize: "0.8rem",
                  color: "#495057", fontWeight: 500,
                }}>
                  📡 Poll #{pollCount}
                </span>
                <span style={{
                  background: "#e9ecef", borderRadius: "20px",
                  padding: "0.25rem 0.75rem", fontSize: "0.8rem",
                  color: "#495057", fontWeight: 500,
                }}>
                  ⏱ {pollInterval}ms interval
                </span>
                <span style={{
                  background: "#fff3cd", borderRadius: "20px",
                  padding: "0.25rem 0.75rem", fontSize: "0.8rem",
                  color: "#856404", fontWeight: 500,
                }}>
                  ⏳ Processing...
                </span>
              </div>
            )}

            {/* ── Status label ── */}
            <p style={{
              marginTop: "0.75rem", fontWeight: 600, fontSize: "0.95rem",
              color: uploadStatus === "complete" ? "#155724" : "#0c5460",
              textAlign: "center",
            }}>
              {uploadStatus === "processing" && "⏳ Processing your file — please wait..."}
              {uploadStatus === "complete" && !syncing && "✅ Processing Complete"}
              {uploadStatus === "complete" && syncing && "🔄 Syncing to local database..."}
            </p>
          </div>
        )}

        {errorDetails.length > 0 && (
          <div className="error-list">
            <h4>⚠️ Error Details:</h4>
            <ul>
              {errorDetails.map((err, idx) => <li key={idx}>{err}</li>)}
            </ul>
          </div>
        )}

        <div className="upload-section">
          <input type="file" accept=".csv, .xlsx" onChange={handleFileChange} disabled={updating} />
          <div className="button-group">
            <button onClick={handleUpdate} disabled={updating || !file || syncing} className="btn-primary">
              <FaSync style={{ marginRight: "6px" }} />
              {updating ? "Updating..." : syncing ? "Syncing..." : "Update File"}
            </button>
            {(file || uploadStatus !== "idle") && (
              <button onClick={handleReset} disabled={updating || syncing} className="btn-secondary">
                Clear
              </button>
            )}
          </div>
        </div>

        {file && uploadStatus === "idle" && (
          <div className="file-info">
            <p>📁 File: <strong>{file.name}</strong></p>
            <p>💾 Size: <strong>{(file.size / 1024).toFixed(2)} KB</strong></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateInventoryCSV;